import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2,
    ArrowRight,
    Play
} from 'lucide-react';
import './Solutions.css';
import '../LandingPage.css'; // For shared dropdown nav styles

// Import the specific planning images
import planningImg from '../../../assets/image_planning_tms.png';
import timelineImg from '../../../assets/timeline_planning.png';
import tmsLogo from '../../../assets/TmsLogoh.png';

const PlanificationIntelligente = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="saas-container">
            {/* Minimalist SaaS Navbar */}
            <nav className="landing-nav saas-nav">
                <div className="nav-container">
                    <div className="nav-logo">
                        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
                            <img src={tmsLogo} alt="Bfret Logo" style={{ height: '32px' }} />
                        </Link>
                    </div>
                    <div className="nav-links">
                        <div className="nav-dropdown">
                            <span className="nav-dropdown-toggle">Solutions</span>
                            <div className="nav-dropdown-menu">
                                <Link to="/solutions/planification-intelligente">Planification intelligente</Link>
                                <Link to="/solutions/suivi-temps-reel">Suivi en temps réel</Link>
                                <Link to="/solutions/documents-centralises">Documents centralisés</Link>
                                <Link to="/solutions/rapports-analytics">Rapports & Analytics</Link>
                                <Link to="/solutions/gestion-ressources">Gestion des ressources</Link>
                                <Link to="/solutions/ai-agent-center">AI Agent Center</Link>
                            </div>
                        </div>
                        <Link to="/contact" className="btn-primary saas-btn-primary" style={{ marginLeft: '1rem' }}>Démo</Link>
                    </div>
                </div>
            </nav>

            {/* Full Hero Introduction */}
            <section style={{ padding: '6rem 5% 4rem', textAlign: 'center', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <span className="saas-feature-tag" style={{ display: 'inline-block', marginBottom: '1.5rem', backgroundColor: '#e0e7ff', color: '#4338ca', padding: '0.5rem 1rem', borderRadius: '9999px', fontWeight: '600' }}>Planification & Dispatch</span>
                <h1 style={{ fontSize: '3.5rem', maxWidth: '1000px', margin: '0 auto 1.5rem', color: '#0f172a', lineHeight: '1.1' }}>
                    Maîtrisez vos opérations d'un seul coup d'œil
                </h1>
                <p style={{ fontSize: '1.25rem', maxWidth: '750px', margin: '0 auto 2.5rem', color: '#475569', lineHeight: '1.6' }}>
                    Fini les tableaux Excel complexes et les oublis. Découvrez une approche élégante pour gérer vos transports avec notre interface à double vue : une timeline pour vos ressources et un puits de commandes intelligent.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <Link to="/contact" className="saas-btn saas-btn-primary" style={{ padding: '0.875rem 2.5rem', fontSize: '1.1rem', borderRadius: '8px' }}>
                        Demander une démo
                    </Link>
                    <a href="#fonctionnalites" className="saas-btn saas-btn-outline" style={{ padding: '0.875rem 2.5rem', fontSize: '1.1rem', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Play size={18} /> Découvrir l'outil
                    </a>
                </div>
            </section>

            {/* Quick Benefits Grid */}
            <section style={{ padding: '4rem 5%', backgroundColor: '#ffffff' }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem' }}>
                    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '1rem' }}>Zéro perte d'information</h3>
                        <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>Toutes vos commandes convergent vers un point unique. Ne laissez plus aucune demande de transport passer inaperçue.</p>
                    </div>
                    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '1rem' }}>Anticipation totale</h3>
                        <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>Grâce à la vue Gantt, visualisez instantanément qui fait quoi, et à quel moment, pour optimiser les temps de trajet.</p>
                    </div>
                    <div style={{ padding: '2rem', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <h3 style={{ fontSize: '1.25rem', color: '#0f172a', marginBottom: '1rem' }}>Réduction des erreurs</h3>
                        <p style={{ color: '#475569', margin: 0, lineHeight: '1.5' }}>Une interface claire avec des indicateurs visuels forts pour vous empêcher de surcharger un véhicule ou d'oublier une livraison.</p>
                    </div>
                </div>
            </section>

            {/* SECTION 1: The Timeline (Start with second picture) */}
            <section id="fonctionnalites" className="saas-feature-section" style={{ paddingTop: '5rem', paddingBottom: '4rem', borderTop: '1px solid #f1f5f9' }}>
                <div className="saas-feature-grid">
                    <div className="saas-feature-text" style={{ paddingRight: '2rem' }}>
                        <span className="saas-feature-tag">Vue Planning</span>
                        <h2 style={{ fontSize: '2.5rem', lineHeight: '1.2', marginBottom: '1.5rem', color: '#0f172a' }}>
                            Pilotez vos ressources avec précision
                        </h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2.5rem', lineHeight: '1.6' }}>
                            La timeline vous offre une représentation visuelle claire de vos opérations. Chaque ligne correspond à un chauffeur ou un véhicule, vous permettant d'avoir une vue parfaite sur l'emploi du temps de votre flotte.
                        </p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Visualisation détaillée
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Affichez avec précision les durées estimées et les affectations sous forme de blocs de temps pour organiser vos journées sans erreur.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Identification des disponibilités
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Repérez instantanément les créneaux libres, les chevauchements d'horaires et les temps morts pour maximiser la rentabilité.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Lecture rapide du planning
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Une interface chronologique intuitive qui facilite la compréhension immédiate des missions affectées au quotidien.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="saas-feature-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', inset: '-10px', background: 'linear-gradient(to right, #e0e7ff, #f3e8ff)', filter: 'blur(30px)', opacity: '0.5', borderRadius: '20px', zIndex: 0 }}></div>
                            <img 
                                src={timelineImg} 
                                alt="Timeline de planification BFRET" 
                                style={{ 
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%', 
                                    maxWidth: '700px',
                                    borderRadius: '12px', 
                                    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)',
                                    border: '1px solid rgba(0,0,0,0.05)'
                                }} 
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* SECTION 2: The Grid (The first picture, reversed layout) */}
            <section className="saas-feature-section alt-bg" style={{ paddingTop: '5rem', paddingBottom: '5rem', backgroundColor: '#f8fafc' }}>
                <div className="saas-feature-grid reverse">
                    <div className="saas-feature-text" style={{ paddingLeft: '2rem' }}>
                        <span className="saas-feature-tag">Le Puits de Commandes</span>
                        <h2 style={{ fontSize: '2.5rem', lineHeight: '1.2', marginBottom: '1.5rem', color: '#0f172a' }}>
                            Centralisez toutes vos demandes
                        </h2>
                        <p style={{ fontSize: '1.15rem', color: '#475569', marginBottom: '2.5rem', lineHeight: '1.6' }}>
                            Une grille de données robuste qui rassemble l'intégralité de vos missions à planifier. Gardez le contrôle total sur votre flux de travail avant de déclencher l'affectation physique.
                        </p>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Vue détaillée des expéditions
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Accédez en un clin d'œil aux lieux de chargement, déchargement, et aux détails de chaque client depuis le tableau.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Statuts et Filtres avancés
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Trouvez instantanément ce que vous cherchez et suivez l'avancement global grâce aux filtres par statut, date ou client.
                                </p>
                            </div>
                            <div>
                                <h3 style={{ fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem', color: '#0f172a', marginBottom: '0.5rem' }}>
                                    <CheckCircle2 size={24} color="var(--saas-primary)" />
                                    Centralisation des flux
                                </h3>
                                <p style={{ fontSize: '1.05rem', color: '#475569', margin: 0, paddingLeft: '2.25rem' }}>
                                    Toutes les commandes arrivent ici, qu'elles soient saisies manuellement ou injectées via des intégrations externes.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="saas-feature-visual" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ position: 'relative' }}>
                            <div style={{ position: 'absolute', inset: '-10px', background: 'linear-gradient(to left, #e0e7ff, #f3e8ff)', filter: 'blur(30px)', opacity: '0.5', borderRadius: '20px', zIndex: 0 }}></div>
                            <img 
                                src={planningImg} 
                                alt="Grille des commandes BFRET" 
                                style={{ 
                                    position: 'relative',
                                    zIndex: 1,
                                    width: '100%', 
                                    maxWidth: '700px',
                                    borderRadius: '12px', 
                                    boxShadow: '0 20px 40px -10px rgba(0, 0, 0, 0.15)',
                                    border: '1px solid rgba(0,0,0,0.05)'
                                }} 
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Metrics Banner */}
            <section style={{ backgroundColor: '#0f172a', color: 'white', padding: '5rem 5%' }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '3rem', textAlign: 'center' }}>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>100%</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Commandes centralisées</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>Vue</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Gantt temps réel</div>
                    </div>
                    <div>
                        <div style={{ fontSize: '3.5rem', fontWeight: 'bold', color: '#818cf8', marginBottom: '0.5rem' }}>0</div>
                        <div style={{ fontSize: '1.1rem', color: '#94a3b8' }}>Perte d'information</div>
                    </div>
                </div>
            </section>

            {/* Footer CTA */}
            <section className="saas-hero" style={{ padding: '6rem 5%', textAlign: 'center', backgroundColor: '#ffffff' }}>
                <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem', color: '#0f172a' }}>Prêt à optimiser l'organisation de votre flotte ?</h2>
                <p style={{ maxWidth: '600px', margin: '0 auto 2.5rem', fontSize: '1.15rem', color: '#475569', lineHeight: '1.6' }}>
                    Passez à la vitesse supérieure en utilisant des outils clairs, visuels et pensés spécifiquement pour le transport de marchandises.
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                    <Link to="/contact" className="saas-btn saas-btn-primary" style={{ padding: '1rem 2.5rem', fontSize: '1.15rem', borderRadius: '8px' }}>
                        Contacter notre équipe <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
                    </Link>
                </div>
            </section>
        </div>
    );
};

export default PlanificationIntelligente;
